// client/src/components/common/StorefrontBanner.js
// Enterprise Feature: Persistent "Back to [Wholesaler]" banner
// Uses sessionStorage — persists across all business pages during the session
// Hides when on a wholesaler detail page, shows on all other business pages

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../../utils/imageHelper';

const STORAGE_KEY = 'wholesalehub_storefront';

/**
 * setStorefrontContext() - Save storefront context to sessionStorage
 */
export const setStorefrontContext = ({ slug, wholesalerName, wholesalerId, logo }) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ slug, wholesalerName, wholesalerId, logo, ts: Date.now() }));
  } catch { /* private browsing may block sessionStorage */ }
};

/**
 * clearStorefrontContext() - Clear storefront context
 */
export const clearStorefrontContext = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
};

/**
 * getStorefrontContext() - Sync read (for non-hook usage)
 */
export const getStorefrontContext = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (data.slug) return { ...data, isFromStorefront: true, storefrontUrl: `/wholesalers/${data.slug}` };
    }
  } catch {}
  return { isFromStorefront: false };
};

/**
 * useStorefrontContext() - Hook version for React components
 */
export const useStorefrontContext = () => {
  const [ctx, setCtx] = useState({ isFromStorefront: false, slug: null, wholesalerName: null, wholesalerId: null, storefrontUrl: null, logo: null });

  useEffect(() => {
    const data = getStorefrontContext();
    if (data.isFromStorefront) {
      setCtx({
        isFromStorefront: true,
        slug: data.slug,
        wholesalerName: data.wholesalerName || data.slug,
        wholesalerId: data.wholesalerId || null,
        storefrontUrl: data.storefrontUrl,
        logo: data.logo || null
      });
    }
  }, []);

  return ctx;
};

/**
 * StorefrontBanner
 * Dark gold (#a29060) banner with wholesaler logo, high contrast text
 * Hides on /wholesalers/:id pages, shows everywhere else
 */
const StorefrontBanner = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const { isFromStorefront, wholesalerName, storefrontUrl, logo } = useStorefrontContext();

  // Hide on wholesaler detail pages
  const isOnWholesalerDetail = /^\/wholesalers\/[^/]+/.test(location.pathname);

  if (!isFromStorefront || isOnWholesalerDetail) return null;

  return (
    <>
      {/* Spacer for fixed mobile navbar — only when banner is showing */}
      <div className="h-14 lg:h-0" />
      <div
        style={{
          background: 'linear-gradient(135deg, #7a6a45 0%, #a29060 50%, #8a7a50 100%)',
          position: 'relative',
          zIndex: 39
        }}
      >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2 sm:py-2.5">
          {/* Left: Arrow + Logo + Name */}
          <Link
            to={storefrontUrl}
            className="flex items-center gap-2 sm:gap-3 min-w-0"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />

            {/* Wholesaler logo — larger */}
            {logo ? (
              <img
                src={getImageUrl(logo)}
                alt=""
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-contain bg-white/90 p-0.5 flex-shrink-0"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-base sm:text-lg">{wholesalerName?.charAt(0) || 'W'}</span>
              </div>
            )}

            <span className="text-sm sm:text-base font-semibold text-white truncate drop-shadow-sm">
              {t('common.backTo', 'Back to')} {wholesalerName}
            </span>
          </Link>

          {/* Right: Powered by WholesaleHub — high contrast */}
          <div className="flex-shrink-0 ml-4 hidden sm:flex items-center gap-1">
            <span className="text-xs text-white/70">Powered by</span>
            <span className="text-xs font-bold text-white">Wholesale</span>
            <span className="text-xs font-bold" style={{ color: '#60a5fa' }}>Hub</span>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default StorefrontBanner;